import { inject, Injectable } from "@angular/core";
import { FgdsProductType } from "@domain/product/product";
import { FgdsDirectBomEntry } from "@domain/project/direct-bom/fgds-direct-bom-entry";
import { FgdsProject } from "@domain/project/fgds-project";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { ProductData } from "@domain/project/product-data/product-data";
import { ProductDataService } from "@domain/project/product-data/product-data.service";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { CostService } from "@domain/project/product-list/cost.service";
import { GroupSection } from "@domain/project/product-list/group-section";
import { ProductListDiscountKey } from "@domain/project/product-list/product-list-discount-key.util";
import { calculateDiscountedPrice } from "@domain/project/product-list/product-list-discount.util";
import { comparePositionThenName, getLatestTopLevelPositionId } from "@domain/project/product-list/product-list-position.util";
import { ProductSection } from "@domain/project/product-list/product-section";
import { ProductListDiscountSection, ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { ALPHABET } from "@utils/alphabet";
import { forkJoin, map, Observable, tap } from "rxjs";
import { GroupItems } from "./group-items";

export interface AdditionalProductEntryConfig {
  id: string;
  name: string;
  mainProductId?: string;
  positionNumber?: string;
  additionalProductListQuantity: number;
  setAdditionalProductListQuantity?(quantity: number): void;
}

export interface AdditionalProductSectionMapping {
  config: AdditionalProductEntryConfig;
  data: ProductData[];
  attachmentInstructions?: ProductData[];
}

export interface AdditionalProductConfigSection {
  config: AdditionalProductEntryConfig;
  productSection: {
    productType: FgdsProductType;
    mappings: AdditionalProductSectionMapping[];
  };
  subtotal: number;
  zrefSubTotal: number;
  isDirectBomEntry?: boolean;
}

@Injectable({ providedIn: "root" })
export class ProductListService {
  private productDataService = inject(ProductDataService);
  private costService = inject(CostService);

  private readonly PRODUCT_TYPES = [
    "alarmDevices",
    "gasWarningCenters",
    "plasticSigns",
    "signalElements",
    "transmitters",
    "flameDetectors",
  ] as const;

  private readonly UNGROUPED_PRODUCTS_TITLE: string = $localize`:@@productList.defaultGroup:Sonstige Produkte`;

  resolveGroupItems(group: ProductItemGroup, floorplans: Floorplan[]): GroupItems {
    const floorplanMap = new Map(floorplans.map((f) => [f.id, f]));

    const findInFloorplan = <T extends { id: string }>(
      floorplanId: string,
      itemId: string,
      getter: (f: Floorplan) => T[],
    ): T | undefined => {
      const floorplan = floorplanMap.get(floorplanId);
      return floorplan ? getter(floorplan).find((d) => d.id === itemId) : undefined;
    };

    const result: GroupItems = {
      alarmDevices: [],
      alarmDevicePlaceholders: [],
      gasWarningCenters: [],
      gasWarningCenterPlaceholders: [],
      plasticSigns: [],
      plasticSignPlaceholders: [],
      signalElements: [],
      signalElementPlaceholders: [],
      transmitters: [],
      transmitterPlaceholders: [],
      flameDetectors: [],
      flameDetectorPlaceholders: [],
    };

    for (const ref of group.references) {
      const id = ref.floorplanProductItemId;
      const fid = ref.floorplanId;

      const alarmDevice = findInFloorplan(fid, id, (f) => f.alarmDevices);
      if (alarmDevice) {
        result.alarmDevices.push(alarmDevice);
        continue;
      }

      const alarmDevicePlaceholder = findInFloorplan(fid, id, (f) => f.alarmDevicePlaceholders);
      if (alarmDevicePlaceholder) {
        result.alarmDevicePlaceholders.push(alarmDevicePlaceholder);
        continue;
      }

      const gasWarningCenter = findInFloorplan(fid, id, (f) => f.gasWarningCenters);
      if (gasWarningCenter) {
        result.gasWarningCenters.push(gasWarningCenter);
        continue;
      }

      const gasWarningCenterPlaceholder = findInFloorplan(fid, id, (f) => f.gasWarningCenterPlaceholders);
      if (gasWarningCenterPlaceholder) {
        result.gasWarningCenterPlaceholders.push(gasWarningCenterPlaceholder);
        continue;
      }

      const plasticSign = findInFloorplan(fid, id, (f) => f.plasticSigns);
      if (plasticSign) {
        result.plasticSigns.push(plasticSign);
        continue;
      }

      const plasticSignPlaceholder = findInFloorplan(fid, id, (f) => f.plasticSignPlaceholders);
      if (plasticSignPlaceholder) {
        result.plasticSignPlaceholders.push(plasticSignPlaceholder);
        continue;
      }

      const signalElement = findInFloorplan(fid, id, (f) => f.signalElements);
      if (signalElement) {
        result.signalElements.push(signalElement);
        continue;
      }

      const signalElementPlaceholder = findInFloorplan(fid, id, (f) => f.signalElementPlaceholders);
      if (signalElementPlaceholder) {
        result.signalElementPlaceholders.push(signalElementPlaceholder);
        continue;
      }

      const transmitter = findInFloorplan(fid, id, (f) => f.transmitters);
      if (transmitter) {
        result.transmitters.push(transmitter);
        continue;
      }

      const transmitterPlaceholder = findInFloorplan(fid, id, (f) => f.transmitterPlaceholders);
      if (transmitterPlaceholder) {
        result.transmitterPlaceholders.push(transmitterPlaceholder);
        continue;
      }

      const flameDetector = findInFloorplan(fid, id, (f) => f.flameDetectors);
      if (flameDetector) {
        result.flameDetectors.push(flameDetector);
        continue;
      }

      const flameDetectorPlaceholder = findInFloorplan(fid, id, (f) => f.flameDetectorPlaceholders);
      if (flameDetectorPlaceholder) {
        result.flameDetectorPlaceholders.push(flameDetectorPlaceholder);
      }
    }

    return result;
  }

  resolveUngroupedItems(floorplan: Floorplan, project: FgdsProject): GroupItems {
    const groupedIds = new Set(
      project.groups.flatMap((g) =>
        g.references.filter((r) => r.floorplanId === floorplan.id).map((r) => r.floorplanProductItemId),
      ),
    );
    return {
      alarmDevices: floorplan.alarmDevices.filter((d) => !groupedIds.has(d.id)),
      alarmDevicePlaceholders: floorplan.alarmDevicePlaceholders.filter((d) => !groupedIds.has(d.id)),
      gasWarningCenters: floorplan.gasWarningCenters.filter((d) => !groupedIds.has(d.id)),
      gasWarningCenterPlaceholders: floorplan.gasWarningCenterPlaceholders.filter((d) => !groupedIds.has(d.id)),
      plasticSigns: floorplan.plasticSigns.filter((d) => !groupedIds.has(d.id)),
      plasticSignPlaceholders: floorplan.plasticSignPlaceholders.filter((d) => !groupedIds.has(d.id)),
      signalElements: floorplan.signalElements.filter((d) => !groupedIds.has(d.id)),
      signalElementPlaceholders: floorplan.signalElementPlaceholders.filter((d) => !groupedIds.has(d.id)),
      transmitters: floorplan.transmitters.filter((d) => !groupedIds.has(d.id)),
      transmitterPlaceholders: floorplan.transmitterPlaceholders.filter((d) => !groupedIds.has(d.id)),
      flameDetectors: floorplan.flameDetectors.filter((d) => !groupedIds.has(d.id)),
      flameDetectorPlaceholders: floorplan.flameDetectorPlaceholders.filter((d) => !groupedIds.has(d.id)),
    };
  }

  createGroupSectionsForProject(project: FgdsProject, floorplans?: Floorplan[]): Observable<GroupSection[]> {
    const targetFloorplans = floorplans ?? project.cachedFloorplans;
    const floorplanName = floorplans?.length === 1 ? floorplans[0].name : undefined;
    const mergedUngroupedItems = this.mergeUngroupedItemsAcrossFloorplans(project, targetFloorplans);
    const ungroupedSection$ = this.createGroupSection(mergedUngroupedItems, floorplanName, undefined, project);

    const groupSections$ = project.groups.map((group) => {
      const items = this.resolveGroupItems(group, targetFloorplans);
      return this.createGroupSection(items, floorplanName, group.name, project);
    });

    const all$ = [ungroupedSection$, ...groupSections$];
    return forkJoin(all$);
  }

  private mergeUngroupedItemsAcrossFloorplans(project: FgdsProject, floorplans?: Floorplan[]): GroupItems {
    const merged: GroupItems = {
      alarmDevices: [],
      alarmDevicePlaceholders: [],
      gasWarningCenters: [],
      gasWarningCenterPlaceholders: [],
      plasticSigns: [],
      plasticSignPlaceholders: [],
      signalElements: [],
      signalElementPlaceholders: [],
      transmitters: [],
      transmitterPlaceholders: [],
      flameDetectors: [],
      flameDetectorPlaceholders: [],
    };

    for (const floorplan of floorplans ?? project.cachedFloorplans) {
      const items = this.resolveUngroupedItems(floorplan, project);
      merged.alarmDevices.push(...items.alarmDevices);
      merged.alarmDevicePlaceholders.push(...items.alarmDevicePlaceholders);
      merged.gasWarningCenters.push(...items.gasWarningCenters);
      merged.gasWarningCenterPlaceholders.push(...items.gasWarningCenterPlaceholders);
      merged.plasticSigns.push(...items.plasticSigns);
      merged.plasticSignPlaceholders.push(...items.plasticSignPlaceholders);
      merged.signalElements.push(...items.signalElements);
      merged.signalElementPlaceholders.push(...items.signalElementPlaceholders);
      merged.transmitters.push(...items.transmitters);
      merged.transmitterPlaceholders.push(...items.transmitterPlaceholders);
      merged.flameDetectors.push(...items.flameDetectors);
      merged.flameDetectorPlaceholders.push(...items.flameDetectorPlaceholders);
    }

    return merged;
  }

  filterForUnspecifiedPlaceholders(placeholders: FloorplanPlaceholder[] = []) {
    return placeholders.filter(
      (placeholder) =>
        (placeholder.products ?? []).length === 0 ||
        (placeholder.products ?? []).some((product) => product.id.trim().length === 0),
    );
  }

  countUnspecifiedPlaceholders(placeholders: FloorplanPlaceholder[] = []) {
    return this.filterForUnspecifiedPlaceholders(placeholders).length;
  }
  private getPriceForCustomProducts(items: { products?: PlaceholderProduct[] }[]): number {
    return items
      .flatMap((item) => item.products ?? [])
      .filter((product) => product.id.trim().length > 0)
      .reduce((total, product) => total + calculateDiscountedPrice(product.price?.price ?? 0, product.discount), 0);
  }

  createGroupSection(
    items: GroupItems,
    floorplanName?: string,
    groupName?: string,
    project?: FgdsProject,
  ): Observable<GroupSection> {
    const name = groupName ?? this.UNGROUPED_PRODUCTS_TITLE;
    const groupSection = new GroupSection(name, floorplanName);

    return this.productDataService.collectGroupData(items).pipe(
      tap((results) => {
        groupSection.createAlarmDevices(items.alarmDevices, items.alarmDevicePlaceholders, results[0]);
        groupSection.createGasWarningCenters(items.gasWarningCenters, items.gasWarningCenterPlaceholders, results[1]);
        groupSection.createPlasticSigns(items.plasticSigns, items.plasticSignPlaceholders, results[2]);
        groupSection.createSignalElements(items.signalElements, items.signalElementPlaceholders, results[3]);
        groupSection.createTransmitters(items.transmitters, items.transmitterPlaceholders, results[4]);
        groupSection.createFlameDetectors(items.flameDetectors, items.flameDetectorPlaceholders, results[5]);

        if (project) {
          this.resolveGroupSectionDiscounts(project, groupSection);
        }

        this.recalculateGroupSectionPrices(groupSection);
      }),
      map(() => groupSection),
    );
  }

  recalculateGroupSectionPrices(groupSection: GroupSection): void {
    groupSection.alarmDevices.subtotal =
      this.getPriceForAlarmDevices(groupSection.alarmDevices.mappings) +
      this.getPriceForCustomProducts([...groupSection.alarmDevices.devices, ...groupSection.alarmDevices.placeholders]);
    groupSection.gasWarningCenters.subtotal =
      this.getPriceForGasWarningCenters(groupSection.gasWarningCenters.mappings) +
      this.getPriceForCustomProducts([...groupSection.gasWarningCenters.devices, ...groupSection.gasWarningCenters.placeholders]);
    groupSection.plasticSigns.subtotal =
      this.getPriceForPlasticSigns(groupSection.plasticSigns.mappings) +
      this.getPriceForCustomProducts([...groupSection.plasticSigns.devices, ...groupSection.plasticSigns.placeholders]);
    groupSection.signalElements.subtotal =
      this.getPriceForSignalElements(groupSection.signalElements.mappings) +
      this.getPriceForCustomProducts([...groupSection.signalElements.devices, ...groupSection.signalElements.placeholders]);
    groupSection.transmitters.subtotal =
      this.getPriceForTransmitters(groupSection.transmitters.mappings) +
      this.getPriceForCustomProducts([...groupSection.transmitters.devices, ...groupSection.transmitters.placeholders]);
    groupSection.flameDetectors.subtotal =
      this.getPriceForFlameDetectors(groupSection.flameDetectors.mappings) +
      this.getPriceForCustomProducts([...groupSection.flameDetectors.devices, ...groupSection.flameDetectors.placeholders]);

    groupSection.alarmDevices.zrefSubTotal = this.getPriceForAlarmDevices(
      groupSection.alarmDevices.mappings,
      this.getPlacedProductsCount,
      true,
    );
    groupSection.gasWarningCenters.zrefSubTotal = this.getPriceForGasWarningCenters(
      groupSection.gasWarningCenters.mappings,
      this.getPlacedProductsCount,
      true,
    );
    groupSection.plasticSigns.zrefSubTotal = this.getPriceForPlasticSigns(
      groupSection.plasticSigns.mappings,
      this.getPlacedProductsCount,
      true,
    );
    groupSection.signalElements.zrefSubTotal = this.getPriceForSignalElements(
      groupSection.signalElements.mappings,
      this.getPlacedProductsCount,
      true,
    );
    groupSection.transmitters.zrefSubTotal = this.getPriceForTransmitters(
      groupSection.transmitters.mappings,
      this.getPlacedProductsCount,
      true,
    );
    groupSection.flameDetectors.zrefSubTotal = this.getPriceForFlameDetectors(
      groupSection.flameDetectors.mappings,
      this.getPlacedProductsCount,
      true,
    );

    groupSection.calculateSubTotal();
    groupSection.calculateZrefSubTotal();
  }

  getAttachmentInstructionsForProject(groupSections: GroupSection[]) {
    const attachmentInstructions: ProductData[] = [];
    groupSections.map((groupsection: GroupSection) => {
      attachmentInstructions.push(...this.getAttachmentInstructionsForGroup(groupsection));
    });
    return this.dedupeAndSortAttachmentInstructions(attachmentInstructions);
  }

  getAttachmentInstructionsForGroup(groupSection: GroupSection) {
    return this.dedupeAndSortAttachmentInstructions(groupSection.getAttachmentInstructionsForGroup());
  }

  getAttachmentInstructionsForAdditionalSections(additionalSections: AdditionalProductConfigSection[] = []) {
    const additionalInstructions = additionalSections.flatMap((section) =>
      section.productSection.mappings.flatMap((mapping) => mapping.attachmentInstructions ?? []),
    );
    return this.dedupeAndSortAttachmentInstructions(additionalInstructions);
  }

  getLatestPositionIdForProducts(groupSections: GroupSection[], additionalSections: AdditionalProductConfigSection[] = []) {
    return getLatestTopLevelPositionId(this.collectPositionIds(groupSections, additionalSections));
  }

  /**
   * Collects the root-level attachment instructions for a project's product list, combining
   * placed-group and additional-section instructions, deduping by product id (keeping the
   * instruction with the earliest position), and sorting the result by position then name.
   *
   * Shared by both the internal Excel export (`ExportProductListBuilder`) and the on-screen
   * product list (`ProductListComponent`) to avoid duplicating this merge logic.
   */
  getRootAttachmentInstructions(
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[],
  ): ProductData[] {
    const rootAttachmentInstructions = new Map<string, ProductData>();

    [
      ...this.getAttachmentInstructionsForProject(groupSections),
      ...this.getAttachmentInstructionsForAdditionalSections(additionalSections),
    ]
      .filter((instruction) => instruction.id)
      .forEach((instruction) => {
        const existingInstruction = rootAttachmentInstructions.get(instruction.id!);
        if (!existingInstruction) {
          rootAttachmentInstructions.set(instruction.id!, instruction);
          return;
        }

        if ((instruction.positionId ?? "").localeCompare(existingInstruction.positionId ?? "") < 0) {
          rootAttachmentInstructions.set(instruction.id!, instruction);
        }
      });

    return Array.from(rootAttachmentInstructions.values()).sort((first, second) =>
      comparePositionThenName(first.positionId, first.name, second.positionId, second.name),
    );
  }

  getProductsWithIdFromPlaceholders(placeholders: FloorplanPlaceholder[] = []) {
    return placeholders.flatMap((placeholder) => placeholder.products ?? []).filter((product) => product.id.trim().length > 0);
  }

  createAdditionalConfigSections(project: FgdsProject): Observable<AdditionalProductConfigSection[]> {
    const directBomEntries = project.directBomEntries.filter((entry) => entry.quantity > 0);
    return forkJoin([
      this.productDataService.collectAdditionalConfigData(project),
      this.productDataService.collectDirectBomData(directBomEntries),
    ]).pipe(
      map(([[alarmDevices, gasWarningCenters, plasticSigns, signalElements, transmitters, flameDetectors], directEntryData]) =>
        [
          ...alarmDevices.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.ALARMDEVICE)),
          ...gasWarningCenters.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.GASWARNINGCENTER)),
          ...plasticSigns.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.PLASTICSIGN)),
          ...signalElements.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.SIGNALELEMENT)),
          ...transmitters.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.TRANSMITTER)),
          ...flameDetectors.map((mapping) => this.createAdditionalConfigSection(mapping, FgdsProductType.FLAME_DETECTOR)),
          ...directBomEntries.map((entry) => this.createDirectBomSection(project, entry, directEntryData.get(entry.id) ?? [])),
        ]
          .map((section) => {
            section.productSection.mappings.forEach((mapping) =>
              this.resolveDiscounts(
                project,
                mapping.data,
                section.isDirectBomEntry ? ProductListDiscountSection.DIRECT_BOM : ProductListDiscountSection.ADDITIONAL,
                mapping.config.id,
              ),
            );
            section.productSection.mappings.forEach((mapping) =>
              this.resolveDiscounts(
                project,
                mapping.attachmentInstructions ?? [],
                ProductListDiscountSection.ATTACHMENT_INSTRUCTIONS,
              ),
            );
            if (!section.isDirectBomEntry) {
              section.subtotal = section.productSection.mappings.reduce(
                (total, mapping) => total + this.calculateAdditionalMappingPrice(mapping, section.productSection.productType),
                0,
              );
              section.zrefSubTotal = section.productSection.mappings.reduce(
                (total, mapping) =>
                  total + this.calculateAdditionalMappingPrice(mapping, section.productSection.productType, true),
                0,
              );
            }
            return section;
          })
          .sort((a, b) =>
            comparePositionThenName(a.config.positionNumber, a.config.name, b.config.positionNumber, b.config.name),
          ),
      ),
    );
  }

  private collectPositionIds(groupSections: GroupSection[], additionalSections: AdditionalProductConfigSection[] = []) {
    const positionIds: string[] = [];

    for (const section of groupSections) {
      for (const productType of this.PRODUCT_TYPES) {
        const group = section[productType];

        group.mappings.forEach((mapping) => {
          positionIds.push(...this.getPositionIdsFromProductData(mapping.data));
        });

        group.placeholders.forEach((placeholder) => {
          positionIds.push(...this.getPositionIdsFromProductData(placeholder.products));
        });
      }
    }

    for (const section of additionalSections) {
      section.productSection.mappings.forEach((mapping) => {
        positionIds.push(...this.getPositionIdsFromProductData(mapping.data));
      });
    }

    return positionIds;
  }

  private getPositionIdsFromProductData(product: ProductData[] | PlaceholderProduct[] = []) {
    const positionIds: string[] = [];
    product.forEach((data) => {
      if (data.id) {
        positionIds.push(data.positionId ? data.positionId : "");
      }
    });
    return positionIds;
  }

  private getPriceForAlarmDevices(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculateAlarmDeviceConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private getPriceForGasWarningCenters(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculateGasWarningCenterConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private getPriceForPlasticSigns(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculatePlasticSignConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private getPriceForSignalElements(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculateSignalElementConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private getPriceForTransmitters(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculateTransmitterConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private getPriceForFlameDetectors(
    mappings: ConfigProductMap[],
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ): number {
    return mappings.reduce(
      (total, mapping) =>
        total + this.costService.calculateFlameDetectorConfigurationsPrice(mapping, getQuantity(mapping), useZrefPrice),
      0,
    );
  }

  private createAdditionalConfigSection(mapping: ConfigProductMap, productType: FgdsProductType): AdditionalProductConfigSection {
    const productSection = new ProductSection([], [], [mapping], productType);
    const subtotal = this.getPriceForMappings([mapping], productType, this.getAdditionalProductsCount);
    const zrefSubTotal = this.getPriceForMappings([mapping], productType, this.getAdditionalProductsCount, true);
    productSection.subtotal = subtotal;
    productSection.zrefSubTotal = zrefSubTotal;
    return {
      config: mapping.config,
      productSection,
      subtotal,
      zrefSubTotal,
    };
  }

  private createDirectBomSection(
    project: FgdsProject,
    entry: FgdsDirectBomEntry,
    products: ProductData[],
  ): AdditionalProductConfigSection {
    const config: AdditionalProductEntryConfig = {
      id: entry.id,
      name: entry.name,
      mainProductId: entry.mainProductId,
      positionNumber: entry.positionNumber,
      additionalProductListQuantity: entry.quantity,
      setAdditionalProductListQuantity: (quantity) => project.setDirectBomEntryQuantity(entry, quantity),
    };
    const data = products.map((product, index) => {
      product.positionId = index === 0 ? entry.positionNumber : `${entry.positionNumber}.${ALPHABET[index - 1] ?? index}`;
      return product;
    });
    this.resolveDiscounts(project, data, ProductListDiscountSection.DIRECT_BOM, entry.id);
    const discountedUnitSubtotal = data.reduce(
      (total, product) =>
        total +
        (product.priceData ? calculateDiscountedPrice(product.priceData.price, product.discount ?? 0) : 0) * product.quantity,
      0,
    );
    const discountedUnitZrefSubtotal = data.some((product) => product.zrefPriceData != null)
      ? data.reduce(
          (total, product) =>
            total +
            (product.zrefPriceData ? calculateDiscountedPrice(product.zrefPriceData.price, product.discount ?? 0) : 0) *
              product.quantity,
          0,
        )
      : undefined;

    return {
      config,
      productSection: {
        productType: entry.productType,
        mappings: [{ config, data }],
      },
      subtotal: discountedUnitSubtotal * entry.quantity,
      zrefSubTotal: discountedUnitZrefSubtotal != null ? discountedUnitZrefSubtotal * entry.quantity : 0,
      isDirectBomEntry: true,
    };
  }

  private getPriceForMappings(
    mappings: ConfigProductMap[],
    productType: FgdsProductType,
    getQuantity: (mapping: ConfigProductMap) => number = this.getPlacedProductsCount,
    useZrefPrice = false,
  ) {
    switch (productType) {
      case FgdsProductType.ALARMDEVICE:
        return this.getPriceForAlarmDevices(mappings, getQuantity, useZrefPrice);
      case FgdsProductType.GASWARNINGCENTER:
        return this.getPriceForGasWarningCenters(mappings, getQuantity, useZrefPrice);
      case FgdsProductType.PLASTICSIGN:
        return this.getPriceForPlasticSigns(mappings, getQuantity, useZrefPrice);
      case FgdsProductType.SIGNALELEMENT:
        return this.getPriceForSignalElements(mappings, getQuantity, useZrefPrice);
      case FgdsProductType.TRANSMITTER:
        return this.getPriceForTransmitters(mappings, getQuantity, useZrefPrice);
      case FgdsProductType.FLAME_DETECTOR:
        return this.getPriceForFlameDetectors(mappings, getQuantity, useZrefPrice);
    }
    return 0;
  }

  private getPlacedProductsCount(mapping: ConfigProductMap) {
    return mapping.placedProductsCount;
  }

  private getAdditionalProductsCount(mapping: ConfigProductMap) {
    return mapping.additionalProductsCount;
  }

  private resolveGroupSectionDiscounts(project: FgdsProject, groupSection: GroupSection): void {
    for (const productType of this.PRODUCT_TYPES) {
      const section = groupSection[productType];
      for (const mapping of section.mappings) {
        this.resolveDiscounts(project, mapping.data, ProductListDiscountSection.PLACED, mapping.config.id);
        this.resolveDiscounts(project, mapping.attachmentInstructions ?? [], ProductListDiscountSection.ATTACHMENT_INSTRUCTIONS);
      }

      section.devices.forEach((device) => this.resolveCustomProductDiscounts(project, device.products, device.id));
      section.placeholders.forEach((placeholder) =>
        this.resolveCustomProductDiscounts(project, placeholder.products, placeholder.id),
      );
    }
  }

  private resolveCustomProductDiscounts(project: FgdsProject, products: PlaceholderProduct[], ownerId: string): void {
    products.forEach((product) => {
      if (!product.id.trim()) {
        return;
      }

      const key: ProductListDiscountKey = {
        section: ProductListDiscountSection.PLACED,
        ownerId,
        slot: ProductListDiscountSlot.PLACEHOLDER_PRODUCT,
        productId: product.id,
      };
      product.discountKey = key;
      product.discount = project.getProductListDiscount(key);
    });
  }

  private resolveDiscounts(
    project: FgdsProject,
    products: ProductData[],
    section: ProductListDiscountSection,
    ownerId?: string,
  ): void {
    products.forEach((product) => {
      if (!product.id || !product.discountSlot) {
        return;
      }
      const key = {
        section,
        ownerId,
        slot: product.discountSlot,
        productId: product.id,
      };
      product.discountKey = key;
      product.discount = project.getProductListDiscount(key);
    });
  }

  private calculateAdditionalMappingPrice(
    mapping: AdditionalProductSectionMapping,
    productType: FgdsProductType,
    useZrefPrice = false,
  ): number {
    return mapping.data.reduce((total, product) => {
      const priceData = useZrefPrice ? product.zrefPriceData : product.priceData;
      if (!priceData) {
        return total;
      }
      const rowQuantity =
        product.type === FgdsProductType.ATTACHMENT || product.type === FgdsProductType.GASWARNINGCENTER ? product.quantity : 1;
      const appliesToType = product.type === productType || product.type === FgdsProductType.ATTACHMENT;
      return appliesToType
        ? total +
            calculateDiscountedPrice(priceData.price, product.discount ?? 0) *
              rowQuantity *
              mapping.config.additionalProductListQuantity
        : total;
    }, 0);
  }

  private dedupeAndSortAttachmentInstructions(attachmentInstructions: ProductData[]) {
    const uniqueInstructions = Array.from(new Map(attachmentInstructions.map((data) => [data.id, data])).values());
    uniqueInstructions.sort((a, b) => (a.positionId ?? "").localeCompare(b.positionId ?? ""));
    return uniqueInstructions;
  }
}
